import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import type { FlowPlaysRow } from '@shared/flow/plays-view';
import { PLAY_MEMBERSHIP_SOURCES } from '@shared/flow/play-people';
import { usePortalDropdown } from '../usePortalDropdown';

/**
 * `PlayRowActionsMenu` — the per-row "Actions ▾" menu on the Plays list
 * (plan `plans/2026-09-10-001-plays-tab-filter-redesign-plan.md` U6). Ports
 * `mini-apps/playhouse/components/CampaignRowActionsMenu.tsx`'s chrome
 * (portal to `document.body`, fixed positioning with flip/clamp,
 * outside-click/Escape/scroll-close, focus-first-item/restore,
 * `runAndClose` stopPropagation) onto semantic tokens instead of
 * `nightshade-*` in light mode. Open/outside-click/Escape/scroll-close/
 * geometry now come from the shared `usePortalDropdown` (Unit 3 dropdown-
 * positioning-logic promotion, D9) — focus-first-item/restore stays local
 * (not part of that shared hook; `mini-apps/**` still cannot import it, so
 * `CampaignRowActionsMenu.tsx` gets it via a `mini-apps/_shared/hooks/`
 * re-export instead).
 *
 * `plans/2026-09-24-005-plays-tab-one-pr-plan.md` U15 (D6/D11) introduced a
 * "Go to <section>" item per People-in-Play section; plan
 * `2026-09-25-002-plays-sections-unify-accordion-plan.md` U5 (R12) replaces
 * ALL of them with ONE item, "Filter for this play" — `onFilterForPlay()` —
 * which does exactly what a row click does (selects the play via `?play=`).
 * The `pendingScroll` "Go to" machinery this used to drive is deleted
 * entirely (U8 removes it from `PlaysSubtab`). Selection is not a toggle, so
 * there is no checked state. The item renders only for play types with a
 * membership source (`PLAY_MEMBERSHIP_SOURCES`, D2 — event invites today):
 * for any other type there is no section to filter for.
 *
 * U8 adds "View Details" (event rows, everyone, read-only) and "Manage
 * Event" (event rows, `canManageEvents` only, editable) as IN-PAGE sidebar
 * opens (`onOpenDetails(play, mode)`, hosted by `PlaysSubtab`).
 *
 * A row with no items at all renders nothing — no trigger that opens an
 * empty menu.
 *
 * Open state is OWNED BY THE PARENT (`open`/`onOpenChange`) — `PlaysSubtab`
 * keeps one `openMenuPlayId` so only one row's menu is open at a time, and
 * hands this component to `FlowTable` as `config.rowAction` (A11). The
 * trigger stops propagation, so opening the menu never also fires the row's
 * own click (which selects the play).
 */

export interface PlayRowActionsMenuProps {
  play: FlowPlaysRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** U5 — "Filter for this play": select this play, identical to clicking the row. */
  onFilterForPlay: () => void;
  /** U8 — whether this rep may see "Manage Event" (marketing/admin only; `PlaysSubtab` computes `isMarketingUser || isAdmin`). "View Details" has no such gate. */
  canManageEvents: boolean;
  /** U8 — opens `PlaysSubtab`'s in-page `CampaignDetailSidebar` for this play's campaign, read-only ('view') or editable ('edit'). Only ever called for `event_invite` rows — the menu renders neither item otherwise. */
  onOpenDetails: (play: FlowPlaysRow, mode: 'view' | 'edit') => void;
}

const ITEM_CLS =
  'w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-content-secondary dark:text-nightshade-300 hover:bg-bg-secondary dark:hover:bg-nightshade-800 transition-colors';

export function PlayRowActionsMenu({ play, open, onOpenChange, onFilterForPlay, canManageEvents, onOpenDetails }: PlayRowActionsMenuProps) {
  // Open state is controlled by the parent (`open`/`onOpenChange`); geometry
  // (right-aligned — this is the table's last, right-most column),
  // outside-click/Escape close, and scroll-to-close all come from the shared
  // `usePortalDropdown` (Unit 3 dropdown-positioning-logic promotion, D9).
  const { triggerRef, menuRef, menuStyle } = usePortalDropdown<HTMLButtonElement, HTMLDivElement>({
    open,
    onOpenChange,
    align: 'right',
    closeOnScroll: true,
  });

  const hasMembership = Boolean(PLAY_MEMBERSHIP_SOURCES[play.play_type]);
  const isEventPlay = play.play_type === 'event_invite';
  const hasItems = hasMembership || isEventPlay;

  // Open/close focus management — not part of `usePortalDropdown` (that hook
  // owns open state + dismissal + geometry only). `triggerRef`/`menuRef` are
  // stable refs returned by that hook, not a local `useRef()` call, so
  // oxlint's stricter exhaustive-deps reimplementation can't trace them back
  // to a ref and flags the `.current` reads below — same false-positive shape
  // as `FlowChatPanel.tsx`'s `newSessionPanelRef` / `RailDock.tsx`'s
  // precedent (real eslint-plugin-react-hooks doesn't flag this; oxlint is
  // the sole enforcer here).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    // `menuStyle.visibility` is included so this effect fires strictly AFTER
    // `useAnchoredPosition`'s `useLayoutEffect` commits the visible style
    // (that `setStyle` call is what causes this re-render) — see D10. On
    // mount with `open` already true, the FIRST render still has the hidden
    // `HIDDEN_STYLE` (menuRef/triggerRef aren't measured yet), so gating on
    // `open` alone (the old deps) let this effect call `.focus()` on a
    // `visibility:hidden` element — a real browser silently refuses that
    // focus call, losing it entirely (regression R18).
    if (open && menuStyle.visibility === 'visible') {
      const firstItem = menuRef.current?.querySelector<HTMLElement>('[role^="menuitem"]');
      firstItem?.focus();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- see doc comment above.
  }, [open, menuStyle.visibility]);

  useEffect(() => {
    if (!open && wasOpenRef.current) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = open;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- see doc comment above.
  }, [open]);

  const runAndClose = (fn: () => void) => (e: React.MouseEvent) => {
    // Load-bearing: this menu is portaled, but React still bubbles the click
    // up the REACT tree into the row's own open-the-menu handler (if any),
    // which would re-open the menu we are closing on the very same click.
    e.stopPropagation();
    onOpenChange(false);
    fn();
  };

  if (!hasItems) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); onOpenChange(!open); }}
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
          open
            ? 'bg-bg-secondary dark:bg-nightshade-800 border-secondary dark:border-nightshade-600 text-content-primary dark:text-nightshade-100'
            : 'border-secondary dark:border-nightshade-600 text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary dark:hover:bg-nightshade-800'
        }`}
      >
        Actions
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={`Row actions — ${play.name}`}
          style={menuStyle}
          className="w-56 py-1 rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-900 shadow-lg overflow-y-auto"
        >
          {hasMembership && (
            <button
              type="button"
              role="menuitem"
              onClick={runAndClose(onFilterForPlay)}
              className={ITEM_CLS}
            >
              <span className="flex-1">Filter for this play</span>
            </button>
          )}

          {isEventPlay && (
            <button
              type="button"
              role="menuitem"
              onClick={runAndClose(() => onOpenDetails(play, 'view'))}
              className={ITEM_CLS}
            >
              <span className="flex-1">View Details</span>
            </button>
          )}

          {isEventPlay && canManageEvents && (
            <button
              type="button"
              role="menuitem"
              onClick={runAndClose(() => onOpenDetails(play, 'edit'))}
              className={ITEM_CLS}
            >
              <span className="flex-1">Manage Event</span>
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
